// @ts-check
import { GEOSERVER_OWS_URL, GEOSERVER_WFS_URL, GEOSERVER_WORKSPACE } from "../config/api.js";
import { STATISTIK_AKTUELL, STATISTIK_DATASETS } from "../config/statistics.js";

/**
 * @typedef {Object} TreeProperties
 * @property {string} lokalenr - Lokale Baumnummer
 * @property {string} baumart - Baumart (z.B. "Eiche")
 * @property {number} hoehe - Höhe in Metern
 * @property {number} umfang - Stammumfang in cm
 * @property {string} vitalitaet - Vitalitätsstufe, z.B. '0 - gut' | '1 - mittel' | '2 - schlecht' | '3 - tot'
 * @property {number} laengengrad - Längengrad (Longitude)
 * @property {number} breitengrad - Breitengrad (Latitude)
 */

/**
 * @typedef {Object} TreeFeature
 * @property {string} type - 'Feature'
 * @property {string} id
 * @property {number[]} bbox - [minLon, minLat, maxLon, maxLat]
 * @property {{type: string, coordinates: number[]}} geometry
 * @property {TreeProperties} properties
 */

/**
 * @typedef {Object} AreaFeature
 * @property {string} type - 'Feature'
 * @property {string} id
 * @property {{type: string, coordinates: number[][][]}} geometry
 * @property {Object} properties
 */

/**
 * @typedef {Object} SchemaProperty
 * @property {string} name - Feldname
 * @property {string} localType - XSD-Datentyp, z.B. 'string' | 'int' | 'double'
 */

const WS = GEOSERVER_WORKSPACE;
const OWS = GEOSERVER_OWS_URL;
const WFS = GEOSERVER_WFS_URL;

const URL_GEOSERVER_ALLE_BAEUME =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:treesimport&outputFormat=application/json`;

const URL_GEOSERVER_EIGENSCHAFTEN_ZU_EINEM_BAUM =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:treesimport&featureID=`;

const URL_GEOSERVER_EIGENSCHAFTEN_FUER_BAUMSUCHE =
  `${WFS}?service=WFS&request=DescribeFeatureType&version=1.1.0&typeName=${WS}:treesimport&outputFormat=application/json`;

const URL_GEOSERVER_EIGENSCHAFTEN_FUER_FLAECHESUCHE =
  `${WFS}?service=WFS&request=DescribeFeatureType&version=1.1.0&typeName=${WS}:geometries&outputFormat=application/json`;

const URL_GEOSERVER_BAEUME_ZU_EIGENSCHAFT_UND_SUCHBEGRIFF =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:treesimport&CQL_FILTER=`;

const URL_GEOSERVER_FLAECHE_ZU_EIGENSCHAFT_UND_SUCHBEGRIFF =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:geometries&CQL_FILTER=`;

const URL_GEOSERVER_ALLE_FLAECHEN =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:geometries&outputFormat=application/json`;

const URL_GEOSERVER_ALLE_GEFAELLTEN_BAEUME =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:gefaelltebaeume&outputFormat=application/json`;

const URL_GEOSERVER_FLAECHE =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:geometries&featureID=`;

const URL_GEOSERVER_ALLE_GEFAELLTEN_BAEUME_STATISTIK =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:gefaelltebaeume`;

const URL_GEOSERVER_ALLE_BAEUME_STATISTIK =
  `${OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=${WS}:`;

const EIGENSCHAFT = "&propertyName=";
const FORMAT = "&outputFormat=application/json";

async function wfsFetch(url, extractor) {
  try {
    const res = await fetch(url);

    if (!res.ok) {
      const body = await res.text();
      throw new Error(`HTTP ${res.status} ${res.statusText}: ${body.slice(0, 200)}`);
    }

    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      const body = await res.text();
      throw new Error(`Unerwartetes Antwortformat: ${body.slice(0, 200)}`);
    }

    const data = await res.json();
    return extractor(data);
  } catch (error) {
    console.error("GeoServer-Fehler: " + error);
    return undefined;
  }
}

function buildLikeFilterUrl(baseUrl, property, searchTerm) {
  return encodeURI(`${baseUrl}${property}+LIKE+'%${searchTerm}%'${FORMAT}`);
}

function filterFeaturesWithGeometry(features) {
  return features.filter((feature) => feature.geometry !== null);
}

/**
 * Gibt alle Eigenschaften eines einzelnen Baums zurück.
 * @param {string} featureID - GeoServer Feature-ID (z.B. 'treesinitial.42')
 * @returns {Promise<TreeFeature>}
 */
export async function fetchTreeById(featureID) {
  featureID = featureID.replace("treesinitial", "treesimport");
  return wfsFetch(
    URL_GEOSERVER_EIGENSCHAFTEN_ZU_EINEM_BAUM + featureID + FORMAT,
    (data) => data.features[0]
  );
}

/**
 * Gibt die verfügbaren Suchfelder (Schema) für Bäume zurück.
 * @returns {Promise<SchemaProperty[]>}
 */
export async function fetchTreeSearchSchema() {
  return wfsFetch(
    URL_GEOSERVER_EIGENSCHAFTEN_FUER_BAUMSUCHE,
    (data) => data.featureTypes[0].properties
  );
}

/**
 * Gibt die verfügbaren Suchfelder (Schema) für Flächen zurück.
 * @returns {Promise<SchemaProperty[]>}
 */
export async function fetchAreaSearchSchema() {
  return wfsFetch(
    URL_GEOSERVER_EIGENSCHAFTEN_FUER_FLAECHESUCHE,
    (data) => data.featureTypes[0].properties
  );
}

/**
 * Sucht Bäume nach einer Eigenschaft mit LIKE-Filter.
 * @param {string} property - Feldname (lowercase), z.B. 'baumart'
 * @param {string} searchTerm - Suchbegriff (wird mit % umschlossen)
 * @returns {Promise<TreeFeature[]>}
 */
export async function fetchTreesByProperty(property, searchTerm) {
  return wfsFetch(
    buildLikeFilterUrl(URL_GEOSERVER_BAEUME_ZU_EIGENSCHAFT_UND_SUCHBEGRIFF, property, searchTerm),
    (data) => data.features
  );
}

/**
 * Sucht Flächen nach einer Eigenschaft mit LIKE-Filter (nur Features mit Geometrie).
 * @param {string} property - Feldname (lowercase)
 * @param {string} searchTerm - Suchbegriff
 * @returns {Promise<AreaFeature[]>}
 */
export async function fetchAreasByProperty(property, searchTerm) {
  return wfsFetch(
    buildLikeFilterUrl(URL_GEOSERVER_FLAECHE_ZU_EIGENSCHAFT_UND_SUCHBEGRIFF, property, searchTerm),
    (data) => filterFeaturesWithGeometry(data.features)
  );
}

/**
 * @returns {Promise<AreaFeature[]>} Alle Flächen (nur Features mit Geometrie)
 */
export async function fetchAllAreas() {
  return wfsFetch(
    URL_GEOSERVER_ALLE_FLAECHEN,
    (data) => filterFeaturesWithGeometry(data.features)
  );
}

/** @returns {Promise<TreeFeature[]>} */
export async function fetchAllDeletedTrees() {
  return wfsFetch(URL_GEOSERVER_ALLE_GEFAELLTEN_BAEUME, (data) => data.features);
}

/**
 * @param {string} featureID
 * @returns {Promise<AreaFeature>}
 */
export async function fetchAreaById(featureID) {
  return wfsFetch(
    URL_GEOSERVER_FLAECHE + featureID + FORMAT,
    (data) => data.features[0]
  );
}

/** @returns {Promise<{features: TreeFeature[], type: string}>} GeoJSON FeatureCollection */
export async function fetchAllTrees() {
  return wfsFetch(encodeURI(URL_GEOSERVER_ALLE_BAEUME), (data) => data);
}

/**
 * @param {string} eigenschaft - Feldname für Statistik-Abfrage
 * @returns {Promise<TreeFeature[]>}
 */
export async function fetchDeletedTreesStatistics(eigenschaft) {
  return wfsFetch(
    URL_GEOSERVER_ALLE_GEFAELLTEN_BAEUME_STATISTIK + EIGENSCHAFT + eigenschaft.toLowerCase() + FORMAT,
    (data) => data.features
  );
}

/**
 * @param {string|number} jahr - Datensatz-Jahr, z.B. '2023' | '2024'
 * @param {string} eigenschaft - Feldname für Statistik-Abfrage
 * @returns {Promise<TreeFeature[]>}
 */
export async function fetchTreesStatistics(jahr, eigenschaft) {
  const datensatz = STATISTIK_DATASETS[jahr] ?? STATISTIK_AKTUELL;
  const eigenschaftKey = eigenschaft.toLowerCase();
  const urlMitEigenschaft =
    encodeURI(URL_GEOSERVER_ALLE_BAEUME_STATISTIK + datensatz + EIGENSCHAFT + eigenschaftKey + FORMAT);
  const urlOhneEigenschaft =
    encodeURI(URL_GEOSERVER_ALLE_BAEUME_STATISTIK + datensatz + FORMAT);

  const datenMitEigenschaft = await wfsFetch(urlMitEigenschaft, (data) => data.features);
  if (Array.isArray(datenMitEigenschaft)) {
    return datenMitEigenschaft;
  }

  return wfsFetch(urlOhneEigenschaft, (data) => data.features);
}
